import { useEffect, useState } from "react";
import {
  getTimelyWagesSummary,
  getHealthCheckSummary,
  getPFSummary,
  getESICSummary,
  getShiftHoursSummary,
  getOvertimeSummary,
  getBasicCTCSummary,
  getSkillLevelSummary,
  //bulkUploadHealthZip,
} from "../../services/labourLaw";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatDateToYYYYMMDD } from "../../Helper/functionHelper";
import useLoader from "../../hooks/useLoader";
import useModal from "../../hooks/useModal";
import BulkHealthUpload from "./BulkHealthUpload.jsx"; 



/* ---------------- INFO CONTENT ---------------- */
const infoContent = {
  wages: {
    title: "🧾 Timely Wages Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card tells you whether employee salaries were paid on time, as required by labour laws.</b></p>
        <p><b>Total:</b> Total number of employees whose salary was due in the selected period.</p>
        <p><b>Paid:</b> Number of employees who received their salary <b>within the legally allowed time</b> (for example, before the 7th/10th of the month, as applicable).</p>
        <p><b>Unpaid:</b> Number of employees whose salary is <b>not yet paid or was delayed</b>, which may lead to <b>wage law non-compliance</b>.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Delayed or unpaid wages can attract <b> penalties, inspections, and employee complaints </b> under the Payment of Wages Act.</p>
      </>
    ),
  },

  health: {
    title: "🩺 Health Check Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card shows the status of mandatory health checks required for certain employees.</b></p>
        <p><b>Total:</b> Employees who are required to undergo a health check as per labour or safety rules.</p>
        <p><b>Completed:</b> Employees who have <b>successfully completed</b> the required medical examination.</p>
        <p><b>Pending:</b>Employees whose health check is <b>overdue or not yet completed</b>.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Health checks are mandatory for employees working in <b>factories, hazardous jobs, night shifts, or specific roles</b>. Missing these can result in <b>remedial notices or penalties</b>.</p>
      </>
    ),
  },

  pf: {
    title: "🧮 PF Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card shows whether statutory PF contributions are being paid correctly.</b></p>
        <p><b>Total:</b> Employees who are <b>eligible for</b> PF in the selected period.</p>
        <p><b>Paying:</b> Employees for whom <b>PF contribution has been deducted and deposited</b>.</p>
        <p><b>Non-Paying:</b> Employees where <b>PF was not deposited</b>, or contribution is missing.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Non-payment or delayed payment of PF can result in <b>heavy interest, penalties, and legal action</b> from authorities.</p>
      </>
    ),
  },

  esic: {
    title: "🧮 ESIC Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card shows whether statutory  ESIC contributions are being paid correctly.</b></p>
        <p><b>Total:</b> Employees who are <b>eligible for</b> ESIC in the selected period.</p>
        <p><b>Paying:</b> Employees for whom <b>ESIC contribution has been deducted and deposited</b>.</p>
        <p><b>Non-Paying:</b> Employees where <b>ESIC was not deposited</b>, or contribution is missing.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Non-payment or delayed payment of ESIC can result in <b>heavy interest, penalties, and legal action</b> from authorities.</p>
      </>
    ),
  },

  shift: {
    title: "⏱️ Shift Hours Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card checks whether employees are working within legally allowed daily or weekly working hours.</b></p>
        <p><b>Total:</b>  Employees whose attendance or shift data was evaluated.</p>
        <p><b>Within Limit:</b> Employees whose working hours are <b>within permitted limits</b> (e.g., 8–9 hours/day).</p>
        <p><b>Exceeded:</b> Employees who worked <b>beyond the allowed hours</b>, without proper overtime rules.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Excessive working hours without compliance can violate <b>Factories Act / Shops & Establishments laws</b> and lead to audit issues.</p>
      </>
    ),
  },

  overtime: {
    title: "⏰ Overtime Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card shows whether overtime work is handled as per law.</b></p>
        <p><b>Total:</b> Employees who worked extra hours beyond their regular shift.</p>
        <p><b>Compliant:</b> Employees whose overtime was <b>properly recorded, approved, and paid</b> as per rules.</p>
        <p><b>Non-Compliant:</b>  Employees where overtime was <b>worked but not paid, not approved, or exceeds legal limits</b>.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Improper overtime handling can cause <b>wage disputes, penalties, and labour inspections</b>.</p>
      </>
    ),
  },

  skill_level: {
    title: "💰 Minimum Wages Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card shows whether employees are being paid at least the minimum wages defined by labour laws based on their skill category.</b></p>
        <p><b>Total:</b> Total number of employees evaluated under minimum wage rules.</p>
        <p><b>Compliant:</b> Employees who are receiving <b>equal to or more than the government-defined minimum wage</b> for their category (Unskilled, Semi-Skilled, Skilled, Highly Skilled).</p>
        <p><b>Non-Compliant:</b>  Employees who are receiving <b>less than the required minimum wage</b>, which is a violation of labour law.</p>
        <p className="fw-bold mt-2">
          👉 How this is calculated:
        </p>
        <p>Each employee is mapped to a <b>skill category</b>, and their salary is compared against the <b>applicable minimum wage (daily/monthly)</b> defined by the government.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Paying below minimum wage can result in <b>legal penalties, inspections, and employee disputes</b>.</p>
      </>
    ),
  },

  basic_ctc: {
    title: "🧾 Salary Structure Compliance – What does this show?",
    content: (
      <>
        <p><br/><b>This card checks whether employee salary structures follow the labour law rule that basic salary should be at least 50% of total CTC.</b></p>
        <p><b>Total:</b> Total number of employees whose salary structure is evaluated.</p>
        <p><b>Compliant:</b> Employees whose <b>Basic Salary is 50% or more of their total CTC</b>, as per labour code guidelines.</p>
        <p><b>Non-Compliant:</b>  Employees whose <b>Basic Salary is less than 50% of CTC</b>, which may lead to non-compliance.</p>
        <p className="fw-bold mt-2">
          👉 How this is calculated:
        </p>
        <p>The system compares<b> Basic Salary vs Total CTC </b>for each employee.</p>
        <p className="fw-bold mt-2">
          👉 Why this matters:
        </p>
        <p>Incorrect salary structure can impact <b>PF, gratuity, and statutory benefits</b>, and may lead to <b>compliance risks or penalties</b>.</p>
      </>
    ),
  },
};

const LabourLawSummary = ({onBulkSuccess }) => {
  const { showLoader, hideLoader } = useLoader();

  const {showModal}=useModal();

  const openModal = (title, content) => {
    showModal(title,content);
  };


  /* ---------------- DATE FILTER ---------------- */
  const getFirstDateOfMonth = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  };

  const [fromDate, setFromDate] = useState(getFirstDateOfMonth());
  const [toDate, setToDate] = useState(new Date());

  /* ---------------- SUMMARY STATES ---------------- */
  const [summary, setSummary] = useState({
    wages: { totalUsers: 0, paidCount: 0, unpaidCount: 0 },
    health: { totalUsers: 0, completedCount: 0, pendingCount: 0 },
    pf: { totalUsers: 0, payingCount: 0, nonPayingCount: 0 },
    esic: { totalUsers: 0, payingCount: 0, nonPayingCount: 0 },
    shift: { totalUsers: 0, nonViolationCount: 0, violationCount: 0 },
    overtime: { totalUsers: 0, validCount: 0, invalidCount: 0 },
    basic_ctc: { totalUsers: 0, validCount: 0, invalidCount: 0 },
    skill_level: { totalUsers: 0, validCount: 0, invalidCount: 0 },
  });

  /* ---------------- FETCH DATA ---------------- */
  const fetchData = async () => {
    try {
      showLoader();
      const payload = {
        fromDate: formatDateToYYYYMMDD(fromDate),
        toDate: formatDateToYYYYMMDD(toDate),
      };

      const [
        shift,
        wages,
        pf,
        esic,
        overtime,
        health,
        basic_ctc,
        skill_level,
      ] = await Promise.all([
        getShiftHoursSummary(payload),
        getTimelyWagesSummary(payload),
        getPFSummary(payload),
        getESICSummary(payload),
        getOvertimeSummary(payload),
        getHealthCheckSummary(payload),
        getBasicCTCSummary(payload),
        getSkillLevelSummary(payload),
      ]);

      setSummary({
        wages: wages.data.data,
        health: health.data.data,
        pf: pf.data.data,
        esic: esic.data.data,
        shift: shift.data.data,
        overtime: overtime.data.data,
        basic_ctc: basic_ctc.data.data,
        skill_level: skill_level.data.data,
      });
    } catch (e) {
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [fromDate, toDate]);

  /* ---------------- CARD CONFIG ---------------- */
  const cards = [
    {
      key: "wages",
      title: "Timely Wages",
      stats: [
        { label: "Total", value: summary.wages.totalUsers, cls: "bg-primary" },
        { label: "Paid", value: summary.wages.paidCount, cls: "bg-success" },
        { label: "Unpaid", value: summary.wages.unpaidCount, cls: "bg-danger" },
      ],
    },
    {
      key: "health",
      title: "Health Check",
      stats: [
        { label: "Total", value: summary.health.totalUsers, cls: "bg-primary" },
        { label: "Completed", value: summary.health.completedCount, cls: "bg-success" },
        { label: "Pending", value: summary.health.pendingCount, cls: "bg-danger" },
      ],
    },
    {
      key: "pf",
      title: "PF",
      stats: [
        { label: "Total", value: summary.pf.totalUsers, cls: "bg-primary" },
        { label: "Paying", value: summary.pf.payingCount, cls: "bg-success" },
        { label: "Non-Paying", value: summary.pf.nonPayingCount, cls: "bg-danger" },
      ],
    },
    {
      key: "esic",
      title: "ESIC",
      stats: [
        { label: "Total Applicable", value: summary.esic.totalUsers, cls: "bg-primary" },
        { label: "Paying", value: summary.esic.payingCount, cls: "bg-success" },
        { label: "Non-Paying", value: summary.esic.nonPayingCount, cls: "bg-danger" },
      ],
    },
    {
      key: "shift",
      title: "Shift Hours",
      stats: [
        { label: "Total", value: summary.shift.totalUsers, cls: "bg-primary" },
        { label: "Within Limit", value: summary.shift.nonViolationCount, cls: "bg-success" },
        { label: "Exceeded", value: summary.shift.violationCount, cls: "bg-danger" },
      ],
    },
    {
      key: "overtime",
      title: "Overtime",
      stats: [
        { label: "Total", value: summary.overtime.totalUsers, cls: "bg-primary" },
        { label: "Compliant", value: summary.overtime.validCount, cls: "bg-success" },
        { label: "Non-Compliant", value: summary.overtime.invalidCount, cls: "bg-danger" },
      ],
    },
    {
      key: "basic_ctc",
      title: "Basic CTC",
      stats: [
        { label: "Total", value: summary.basic_ctc.totalUsers, cls: "bg-primary" },
        { label: "Compliant", value: summary.basic_ctc.payingCount, cls: "bg-success" },
        { label: "Non-Compliant", value: summary.basic_ctc.nonPayingCount, cls: "bg-danger" },
      ],
    },
    {
      key: "skill_level",
      title: "Skill Level Wages",
      stats: [
        { label: "Total", value: summary.skill_level.totalUsers, cls: "bg-primary" },
        { label: "Compliant", value: summary.skill_level.payingCount, cls: "bg-success" },
        { label: "Non-Compliant", value: summary.skill_level.nonPayingCount, cls: "bg-danger" },
      ],
    },
  ];

  return (
    <>
      {/* DATE FILTER */}
      <div className="row mb-3 align-items-end mt-2">
        {/* LEFT SIDE – DATE FILTER */}
        <div className="col-md-6 d-flex gap-3 mt-2">
          <div className="flex-fill">
            <label className="form-label fw-bold mb-1">From Date</label>
            <DatePicker
              value={fromDate}
              format="dd MMM yyyy"
              maxDate={toDate}
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </div>

          <div className="flex-fill">
            <label className="form-label fw-bold mb-1">To Date</label>
            <DatePicker
              value={toDate}
              format="dd MMM yyyy"
              minDate={fromDate}
              maxDate={new Date()}
              onChange={(newValue) => setToDate(newValue)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </div>
        </div>

        {/* RIGHT SIDE – BULK UPLOAD */}
        <div className="col-md-6 text-end">
          <button
            className="btn btn-success"
            onClick={() =>
              showModal(
                "Bulk Add/Update Health Details",
                <BulkHealthUpload
                  onSuccess={() => {
                    fetchData();
                    onBulkSuccess?.();
                  }}
                />
              )
            }
          >
            Bulk Add/Update Health Details
          </button>

        </div>
      </div>



      {/* CARDS */}
      <div className="row">
        {cards.map((card) => (
          <div key={card.key} className="col-lg-3 col-md col-sm mb-3">
            <div className="card h-100 shadow-sm">
              <div className="card-header d-flex justify-content-between fw-bold">
                {card.title}
                <Tooltip title="View compliance explanation">
                  <InfoOutlinedIcon
                    className="cursor-pointer"
                    onClick={() =>
                      openModal(
                        infoContent[card.key].title,
                        infoContent[card.key].content
                      )
                    }
                  />
                </Tooltip>
              </div>

              <div className="card-body">
                {card.stats.map((s, i) => (
                  <div key={i} className="d-flex justify-content-between mt-2">
                    <span>{s.label}</span>
                    <span className={`badge ${s.cls} text-white`}>
                      {s.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

    </>
  );
};

export default LabourLawSummary;